import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store/useStore';
import { useAuthStore } from '../store/authStore';
import { CarCartIcon, CarSearchIcon, CarMenuIcon } from './CarIcons';
import AuthModal from './AuthModal';
import { User, LogOut } from 'lucide-react';
import logo from '../assets/logo.png';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { setSearchOpen, setCartOpen, getCartCount } = useStore();
  const cartCount = useStore((s) => s.cart.reduce((acc, item) => acc + item.quantity, 0));
  const { user, isAuthenticated, logout } = useAuthStore();

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/magaza', label: t('nav.shop') },
    { path: '/favorilerim', label: t('nav.favorites') },
    { path: '/kargo-takip', label: t('nav.tracking') },
    { path: '/iletisim', label: t('nav.contact') },
  ];

  return (
    <header className="sticky top-0 z-50">
      {/* Scrolling Banner */}
      <div className="bg-gradient-to-r from-[#E31E24] via-[#FF4500] to-[#E31E24] text-white overflow-hidden">
        <div className="animate-marquee whitespace-nowrap py-1.5">
          <span className="inline-block mx-8 text-xs sm:text-sm font-medium">
            🚗 {t('banner.freeCargo')} &nbsp;|&nbsp; ⚡ {t('banner.kdv')}
          </span>
          <span className="inline-block mx-8 text-xs sm:text-sm font-medium">
            🚗 {t('banner.freeCargo')} &nbsp;|&nbsp; ⚡ {t('banner.kdv')}
          </span>
          <span className="inline-block mx-8 text-xs sm:text-sm font-medium">
            🚗 {t('banner.freeCargo')} &nbsp;|&nbsp; ⚡ {t('banner.kdv')}
          </span>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-[#0A0A0A]/95 backdrop-blur-md border-b border-[#E31E24]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0">
              <img src={logo} alt="T-RaX Car Care" className="h-10 sm:h-14 w-auto" />
              <div className="hidden sm:block">
                <h1 className="text-white font-black text-lg tracking-wider">T-RAX</h1>
                <p className="text-[#E31E24] text-[10px] tracking-widest uppercase">Car Care</p>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${
                    location.pathname === link.path
                      ? 'bg-[#E31E24] text-white shadow-lg shadow-[#E31E24]/30'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2 text-gray-300 hover:text-[#E31E24] transition-colors"
                aria-label="Search"
              >
                <CarSearchIcon size={22} />
              </button>

              <button
                onClick={() => setCartOpen(true)}
                className="relative p-2 text-gray-300 hover:text-[#E31E24] transition-colors"
                aria-label="Cart"
              >
                <CarCartIcon size={22} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#E31E24] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Auth Button */}
              {isAuthenticated && user ? (
                <div className="relative group">
                  <button className="flex items-center gap-2 px-3 py-2 text-gray-300 hover:text-[#E31E24] transition-colors text-sm font-medium">
                    <div className="w-7 h-7 bg-[#E31E24] rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{user.firstName.charAt(0)}</span>
                    </div>
                    <span className="hidden xl:inline">{user.firstName}</span>
                  </button>
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[180px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <button
                      onClick={() => navigate('/hesabim')}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <User className="w-4 h-4" /> Hesabım
                    </button>
                    <button
                      onClick={() => { logout(); navigate('/'); }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" /> Çıkış Yap
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-[#E31E24] text-white rounded-xl text-sm font-semibold hover:bg-[#c41a1f] transition-colors"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Giriş Yap</span>
                </button>
              )}

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-gray-300 hover:text-[#E31E24] transition-colors"
                aria-label="Menu"
              >
                <CarMenuIcon size={22} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#0A0A0A]/98 backdrop-blur-md border-b border-[#E31E24]/20">
          <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
                  location.pathname === link.path
                    ? 'bg-[#E31E24] text-white'
                    : 'text-gray-300 hover:bg-white/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {/* Mobile Auth */}
            {!isAuthenticated && (
              <button
                onClick={() => { setMobileMenuOpen(false); setShowAuthModal(true); }}
                className="px-4 py-3 rounded-lg text-sm font-semibold bg-[#E31E24] text-white text-center"
              >
                Giriş Yap / Kayıt Ol
              </button>
            )}
            {isAuthenticated && user && (
              <>
                <button
                  onClick={() => { setMobileMenuOpen(false); navigate('/hesabim'); }}
                  className="px-4 py-3 rounded-lg text-sm font-semibold text-gray-300 hover:bg-white/10 text-left"
                >
                  👤 Hesabım
                </button>
                <button
                  onClick={() => { setMobileMenuOpen(false); logout(); navigate('/'); }}
                  className="px-4 py-3 rounded-lg text-sm font-semibold text-red-400 hover:bg-white/10 text-left"
                >
                  🚪 Çıkış Yap
                </button>
              </>
            )}
          </nav>
        </div>
      )}

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </header>
  );
};

export default Header;
